'use client';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import cn from '@/lib/clsxm';

import Container from '@/components/@base/container';
import FeedbackSidebar from '@/components/app/demo/components/feedback-sidebar';
import Others from '@/components/app/demo/components/others';
import { useGetProductDemo } from '@/components/app/demo/services/hooks';

import { DEMO_VIEW_MODE, useProductDemoStore } from '@/store/product-demo.store';

export default function DemoComponent({ slug }: { slug: string }) {
  const { data } = useGetProductDemo(slug);
  const { viewMode, isHeaderVisible, showOthers } = useProductDemoStore();
  const [dimensions, setDimensions] = useState('100%');
  const searchParams = useSearchParams();
  const [specificDemoUrl, setSpecificDemoUrl] = useState<string | null>(null);
  const [isSidebarOpen, setSidebarOpen] = useState(false); // Sidebar open state

  useEffect(() => {
    const getIframeDimensions = () => {
      switch (viewMode) {
        case DEMO_VIEW_MODE.DESKTOP:
          return '100%';
        case DEMO_VIEW_MODE.TABLET:
          return '60%';
        case DEMO_VIEW_MODE.MOBILE:
          return '30%';
        default:
          return '100%';
      }
    };

    setDimensions(getIframeDimensions());
  }, [viewMode]);

  useEffect(() => {
    if (data && data.demos) {
      const demoId = searchParams.get('demo_id');
      const demoItem = data.demos.find((item) => item.demoId === demoId);
      if (demoItem && demoItem.previewUrl) {
        setSpecificDemoUrl(demoItem.previewUrl);
      } else {
        setSpecificDemoUrl(null);
      }
    }
  }, [data, searchParams]);

  if (!data) {
    return null;
  }

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const closeSidebarOnOverlayClick = () => {
    setSidebarOpen(false);
  };

  return (
    <Container center className='w-full'>
      <Others
        data={data.demos}
        productPrice={data.price}
        productLastPrice={data.lastPrice}
        productIcon={data.icon}
        productId={data.id}
        productSlug={data.slug}
        show={showOthers && isHeaderVisible}
      />
      <Container
        className={cn(
          'iframe-container',
          isHeaderVisible ? 'h-[calc(100vh-74px)] md:h-[calc(100vh-100px)]' : 'h-dvh',
        )}
      >
        <FeedbackSidebar
          productId={data.id}
          title={data.title}
          isOpen={isSidebarOpen}
          toggleSidebar={toggleSidebar}
          className={
            isHeaderVisible ? 'h-[calc(100vh-74px)] md:h-[calc(100vh-100px)]' : 'h-dvh'
          }
        />
        {/*overlay*/}
        {isSidebarOpen && (
          <Container
            className={cn(
              'absolute bottom-0 left-0 right-0 z-40 bg-[#000] bg-opacity-40',
              isHeaderVisible ? 'h-[calc(100vh-74px)] md:h-[calc(100vh-100px)]' : 'h-dvh',
            )}
            onClick={closeSidebarOnOverlayClick}
          />
        )}

        <iframe
          src={specificDemoUrl || data.demoUrl}
          className={`demo-iframe ${viewMode} ${viewMode === DEMO_VIEW_MODE.DESKTOP && 'full'}`}
          width={dimensions}
          title='Demo Preview'
        />
      </Container>
    </Container>
  );
}
